import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ExerciseEditor from "@/components/ExerciseEditor";
import { Dumbbell, Zap, Flame, Home as HomeIcon, Pencil, Plus, Check, TrendingUp } from "lucide-react";

const HOME_DAYS = [
  { section: "home_d1", label: "Mon · Full Body" },
  { section: "home_d2", label: "Tue · Cardio & Core" },
  { section: "home_d3", label: "Wed · Upper Body" },
  { section: "home_d4", label: "Thu · Lower Body" },
  { section: "home_d5", label: "Fri · HIIT Blast" },
  { section: "home_d6", label: "Sat · Yoga & Mobility" },
  { section: "home_d7", label: "Sun · Active Recovery" },
];

const PPL_SECTIONS = [
  { section: "push", label: "Push" },
  { section: "pull", label: "Pull" },
  { section: "legs", label: "Legs" },
];

const TABS = [
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "ppl", label: "PPL", icon: Dumbbell },
  { key: "ronaldo", label: "CR7", icon: Zap },
  { key: "hiit", label: "HIIT", icon: Flame },
];

function ProgressBadge({ progress }) {
  if (!progress) return null;
  const { sessions, tier, sessions_to_next_tier } = progress;
  const pct = ((7 - sessions_to_next_tier) / 7) * 100;
  return (
    <div className="flex items-center gap-3 shrink-0" data-testid="progress-badge">
      <div className="text-right">
        <div className="text-[0.6rem] font-mono-tech text-cyan-400">
          TIER {tier} · {sessions} sessions
        </div>
        <div className="w-24 h-1.5 bg-white/10 mt-1 relative">
          <div
            className="h-full bg-gradient-to-r from-violet-500 to-cyan-400"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-[0.55rem] text-slate-500 font-mono-tech mt-0.5">
          {sessions_to_next_tier === 7 ? "Ready" : `${sessions_to_next_tier} to next tier`}
        </div>
      </div>
      <TrendingUp size={16} className="text-cyan-400" />
    </div>
  );
}

function SectionBlock({ label, section, exercises, progress, onEdit, onAdd, onComplete }) {
  return (
    <div className="border border-white/10 bg-black/30 p-4" data-testid={`section-${section}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="h-display text-lg text-violet-300 uppercase">{label}</div>
        <ProgressBadge progress={progress} />
      </div>
      <ul className="space-y-1.5 mb-3">
        {exercises.length === 0 && (
          <li className="text-xs text-slate-500 py-2">No exercises yet. Add one below.</li>
        )}
        {exercises.map((ex) => (
          <li
            key={ex.id}
            className="flex items-center gap-2 border-b border-white/5 py-2"
            data-testid={`exercise-${ex.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{ex.name}</div>
              <div className="text-[0.6rem] font-mono-tech text-cyan-400">
                {ex.sets} × {ex.reps}
              </div>
            </div>
            <button
              data-testid={`exercise-edit-${ex.id}`}
              onClick={() => onEdit(ex)}
              className="shrink-0 p-1.5 border border-white/10 text-slate-400 hover:border-cyan-400/60 hover:text-cyan-300"
              title="Edit"
            >
              <Pencil size={12} />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button
          data-testid={`add-exercise-${section}`}
          onClick={() => onAdd(section)}
          className="btn-purple !text-xs !px-3 !py-1.5 flex-1"
        >
          <Plus size={12} className="inline mr-1" /> Add Exercise
        </button>
        <button
          data-testid={`complete-session-${section}`}
          onClick={() => onComplete(section)}
          className="btn-neon !text-xs !px-3 !py-1.5"
        >
          <Check size={12} className="inline mr-1" /> Complete
        </button>
      </div>
    </div>
  );
}

export default function Workouts() {
  const { refresh } = useAuth();
  const [tab, setTab] = useState("home");
  const [data, setData] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editSection, setEditSection] = useState(null);

  const load = async () => {
    const r = await api.get("/workouts/all");
    setData(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const onEdit = (ex) => {
    setEditing(ex);
    setEditSection(ex.section);
    setEditorOpen(true);
  };

  const onAdd = (section) => {
    setEditing(null);
    setEditSection(section);
    setEditorOpen(true);
  };

  const onComplete = async (section) => {
    try {
      const r = await api.post("/workouts/session", { section });
      if (r.data.leveled_up) {
        toast.success(`⚡ LEVEL UP! You are now LVL ${r.data.new_level}`, { duration: 4000 });
      } else {
        toast.success(`+${r.data.xp_earned} XP · Session complete`);
      }
      if (r.data.new_tier_unlocked) {
        toast.success(`⚔️ Tier ${r.data.tier} unlocked — time to increase intensity!`, {
          duration: 6000,
        });
      }
      load();
      refresh();
    } catch {
      toast.error("Failed to log session");
    }
  };

  if (!data) return <div className="label-tech animate-pulse">Loading protocol...</div>;

  const sections = data.sections;
  const progress = data.progress;

  return (
    <div className="space-y-6" data-testid="workouts-page">
      <div>
        <div className="label-tech">Training Protocol</div>
        <h1 className="h-display text-3xl sm:text-4xl mt-1">
          <span className="text-cyan-400">Iron</span> Ascend
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Home 7-day split · PPL · CR7 · HIIT — fully editable. Every 7 sessions unlocks a new tier.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 px-3 py-2.5 font-display uppercase text-xs tracking-widest flex items-center justify-center gap-2 ${
              tab === t.key
                ? "bg-cyan-400 text-black"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "home" && (
        <section className="panel panel-corners p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <HomeIcon size={16} className="text-cyan-400" />
            <div className="label-tech">7-Day Home Workout</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {HOME_DAYS.map((d) => (
              <SectionBlock
                key={d.section}
                label={d.label}
                section={d.section}
                exercises={sections[d.section] || []}
                progress={progress[d.section]}
                onEdit={onEdit}
                onAdd={onAdd}
                onComplete={onComplete}
              />
            ))}
          </div>
        </section>
      )}

      {tab === "ppl" && (
        <section className="panel panel-corners p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Dumbbell size={16} className="text-cyan-400" />
            <div className="label-tech">Push · Pull · Legs</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PPL_SECTIONS.map((s) => (
              <SectionBlock
                key={s.section}
                label={s.label}
                section={s.section}
                exercises={sections[s.section] || []}
                progress={progress[s.section]}
                onEdit={onEdit}
                onAdd={onAdd}
                onComplete={onComplete}
              />
            ))}
          </div>
        </section>
      )}

      {tab === "ronaldo" && (
        <section className="panel panel-corners p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={16} className="text-yellow-400" />
            <div className="label-tech">CR7 Conditioning</div>
          </div>
          <SectionBlock
            label="Ronaldo Conditioning"
            section="ronaldo"
            exercises={sections.ronaldo || []}
            progress={progress.ronaldo}
            onEdit={onEdit}
            onAdd={onAdd}
            onComplete={onComplete}
          />
        </section>
      )}

      {tab === "hiit" && (
        <section className="panel panel-corners p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={16} className="text-red-400" />
            <div className="label-tech">HIIT Catalyst</div>
          </div>
          <SectionBlock
            label="HIIT"
            section="hiit"
            exercises={sections.hiit || []}
            progress={progress.hiit}
            onEdit={onEdit}
            onAdd={onAdd}
            onComplete={onComplete}
          />
        </section>
      )}

      <ExerciseEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        exercise={editing}
        section={editSection}
        onSaved={load}
      />
    </div>
  );
}
